import React, { useState, useEffect } from 'react';
import { getTrainingPrograms } from '../services/api';
import {
  FullScreenModal,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
} from './ui/fullscreen-modal';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dumbbell, Plus, X } from 'lucide-react';
import ExercisePlanModal from './ExercisePlanModal';

const PlanAssignmentModal = ({ 
  show, 
  onHide, 
  onPlanSelect, 
  onManualCreate, 
  trainerId,
  currentPlanId = null 
}) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExercisePlanModal, setShowExercisePlanModal] = useState(false);

  useEffect(() => {
    if (show && trainerId) {
      loadPlans();
    }
  }, [show, trainerId]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await getTrainingPrograms(trainerId);
      setPlans(response.data || []);
    } catch (error) {
      console.error('Ошибка загрузки планов:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanAdd = (plan) => {
    if (onPlanSelect) {
      onPlanSelect(plan);
    }
    onHide();
  };

  const handleManualCreate = (exercises) => {
    if (onManualCreate) {
      onManualCreate(exercises);
    }
    setShowExercisePlanModal(false);
    onHide();
  };

  return (
    <>
      <FullScreenModal open={show} onOpenChange={onHide}>
        <FullScreenModalContent className="flex flex-col">
          <FullScreenModalHeader>
            <FullScreenModalBackButton onClick={onHide} />
            <FullScreenModalTitle>Выбор плана</FullScreenModalTitle>
            <div className="w-10" /> {/* Spacer for centering */}
          </FullScreenModalHeader>

          <FullScreenModalBody>
            <div className="space-y-6 pb-6">
              {/* Раздел: Выбрать из шаблонов */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Выбрать из шаблонов</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Выберите сохраненный план тренировки, чтобы автоматически добавить все упражнения
                  </p>
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : plans.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-6 text-center">
                      <Dumbbell className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
                      <p className="text-muted-foreground">Нет сохраненных планов</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Создайте план вручную или сохраните его для использования в будущем
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {plans.map((plan) => {
                      const isSelected = currentPlanId === plan.id;
                      const exerciseCount = plan.exercises?.length || 0;
                      return (
                        <Card
                          key={plan.id}
                          className={`cursor-pointer transition-colors hover:bg-accent ${
                            isSelected ? 'border-primary bg-primary/5' : ''
                          }`}
                          onClick={() => handlePlanAdd(plan)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Dumbbell className="h-4 w-4 text-primary flex-shrink-0" />
                                  <h4 className="font-semibold truncate">{plan.name}</h4>
                                  {isSelected && (
                                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                                      Текущий
                                    </span>
                                  )}
                                </div>
                                {plan.description && (
                                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                    {plan.description}
                                  </p>
                                )}
                                <div className="text-xs text-muted-foreground">
                                  {exerciseCount} {exerciseCount === 1 ? 'упражнение' : exerciseCount < 5 ? 'упражнения' : 'упражнений'}
                                </div>
                              </div>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePlanAdd(plan);
                                }}
                                className="ml-4 flex-shrink-0"
                                disabled={exerciseCount === 0}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Добавить
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Раздел: Создать план с нуля */}
              {onManualCreate && (
                <div className="space-y-4 pt-6 border-t">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Создать план с нуля</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Добавьте упражнения вручную для этой тренировки
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-auto py-6"
                    onClick={() => setShowExercisePlanModal(true)}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Plus className="h-6 w-6" />
                      <span className="text-base font-medium">Добавить упражнения вручную</span>
                    </div>
                  </Button>
                </div>
              )}
            </div>
          </FullScreenModalBody>
        </FullScreenModalContent>
      </FullScreenModal>

      {/* Модал для ручного создания плана */}
      <ExercisePlanModal
        show={showExercisePlanModal}
        onHide={() => setShowExercisePlanModal(false)}
        exercises={[]}
        onSave={handleManualCreate}
        trainerId={trainerId}
      />
    </>
  );
};

export default PlanAssignmentModal;


